import { useEffect, useState } from 'react'

import { AxiosInstance } from 'axios'
import { isBefore } from 'date-fns'

import { NotificationItemState, NotificationItemStatus } from '../../../models/user/User'

interface UseEapHpiBannerStateProps {
  userLyraId: string
  axiosInstance: AxiosInstance
  isEapHpiInProductNotificationsEnabled: boolean
  eapHpiNotificationState: NotificationItemState | undefined
}

export enum EapHpiNotificationType {
  ADD_HEALTH_PLAN = 'add_health_plan',
  ADD_PAYMENT_INFO = 'add_payment_info',
  VIEW_COST_ESTIMATE = 'view_cost_estimate',
}

export type EapToHpiBannerStateResponse = {
  expirationDate: string
  flowAs: string
  notificationType: EapHpiNotificationType
  providerLyraId: string
  sessionType: string
  sessionsRemaining: number

  // Optional fields related to dependents
  childLyraId?: string
  dependentName?: string
}

export type EapToHpiBannerState = Omit<EapToHpiBannerStateResponse, 'sessionsRemaining'> & { sessionsRemaining: 1 | 0 }

// Helper to just create an empty object for the hook
type Undefinified<T> = {
  [K in keyof T]: undefined
}

export type EapToHpiBannerLoading = {
  isEapHpiBannerStateLoading: true
  isEapHpiBannerVisible: false
} & Undefinified<EapToHpiBannerState>

export type EapToHpiBannerVisible = {
  isEapHpiBannerStateLoading: false
  isEapHpiBannerVisible: true
} & EapToHpiBannerState

export type EapToHpiBannerNotVisible = {
  isEapHpiBannerStateLoading: false
  isEapHpiBannerVisible: false
} & Undefinified<EapToHpiBannerState>

export type EapToHpiBannerStatus = EapToHpiBannerLoading | EapToHpiBannerVisible | EapToHpiBannerNotVisible

export const useEapToHpiBannerState = ({
  userLyraId,
  axiosInstance,
  isEapHpiInProductNotificationsEnabled,
  eapHpiNotificationState,
}: UseEapHpiBannerStateProps) => {
  const [isEapHpiBannerStateLoading, setIsEapHpiBannerStateLoading] = useState(true)
  const [isEapHpiBannerVisible, setIsEapHpiBannerVisible] = useState(false)
  const [eapHpiBannerState, setEapHpiBannerState] = useState<EapToHpiBannerState | null>(null)

  const clearState = () => {
    setIsEapHpiBannerVisible(false)
    setEapHpiBannerState(null)
  }

  useEffect(() => {
    const fetch = () => {
      axiosInstance
        .get<EapToHpiBannerStateResponse>(`/v1/user/${userLyraId}/eapHpiBannerData`)
        .then((response) => {
          if (response.data != null && response.data.sessionsRemaining <= 1) {
            const expirationDate = new Date(response.data.expirationDate)
            setIsEapHpiBannerVisible(!isBefore(expirationDate, new Date()))
            setEapHpiBannerState({
              expirationDate: response.data.expirationDate,
              flowAs: response.data.flowAs,
              notificationType: response.data.notificationType,
              providerLyraId: response.data.providerLyraId,
              sessionType: response.data.sessionType,
              sessionsRemaining: response.data.sessionsRemaining as 0 | 1,
              childLyraId: response.data.childLyraId,
              dependentName: response.data.dependentName,
            })
          } else {
            clearState()
          }
        })
        .catch(() => {
          // API responds with 404 if no banner data is found
          clearState()
        })
        .finally(() => {
          setIsEapHpiBannerStateLoading(false)
        })
    }

    const shouldFetchFromNotificationState =
      eapHpiNotificationState &&
      (eapHpiNotificationState == NotificationItemStatus.NEW || eapHpiNotificationState == NotificationItemStatus.SEEN)

    if (isEapHpiInProductNotificationsEnabled && shouldFetchFromNotificationState) {
      fetch()
    } else {
      clearState()
      setIsEapHpiBannerStateLoading(false)
    }
  }, [userLyraId, axiosInstance, isEapHpiInProductNotificationsEnabled, eapHpiNotificationState])

  return {
    isEapHpiBannerStateLoading,
    isEapHpiBannerVisible,
    expirationDate: eapHpiBannerState?.expirationDate,
    flowAs: eapHpiBannerState?.flowAs,
    notificationType: eapHpiBannerState?.notificationType,
    providerLyraId: eapHpiBannerState?.providerLyraId,
    sessionType: eapHpiBannerState?.sessionType,
    sessionsRemaining: eapHpiBannerState?.sessionsRemaining,
    childLyraId: eapHpiBannerState?.childLyraId,
    dependentName: eapHpiBannerState?.dependentName,
  } as EapToHpiBannerStatus
}
